import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

import {
  SelectWrapper,
  SelectTrigger,
  TriggerText,
  ChevronWrap,
  ContactList,
  ContactItem,
  ContactName,
  ContactMeta,
} from './styles';

export type Contact = {
  id: string;
  name: string;
  phone: string;
  email: string;
};

type Rect = {
  topDown: number;
  topUp: number;
  left: number;
  width: number;
  openUp: boolean;
  maxHeight: number;
};

let _uid = 0;

export type ContactSelectProps = {
  /** Lista de contatos. */
  contacts: Contact[];
  /** Id do contato selecionado (controlado). */
  value?: string | null;
  /** Disparado ao selecionar um contato. */
  onChange?: (contact: Contact) => void;
  /** Texto do trigger quando nada selecionado. @default 'Selecione' */
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ChevronIcon = (): React.ReactElement => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 5.5L8 10.5L13 5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const formatMeta = (c: Contact): string => `${c.phone} · ${c.email}`;

/**
 * ContactSelect — seletor de contato com itens ricos (nome + telefone · email).
 *
 * O `Dropdown` genérico só aceita options `{value,label}` string, por isso este
 * componente existe à parte. Mesma mecânica de portal + teclado (↑↓ Enter Esc) +
 * fechar ao clicar fora. NÃO inclui "cadastrar novo contato" (decisão de PO).
 *
 * @example
 * <ContactSelect contacts={contatos} value={sel} onChange={(c) => setSel(c.id)} fullWidth />
 */
export const ContactSelect = ({
  contacts,
  value = null,
  onChange,
  placeholder = 'Selecione',
  fullWidth = false,
  disabled = false,
  id: idProp,
  className,
  style,
}: ContactSelectProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [rect, setRect] = useState<Rect | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const uid = useRef(`contact-select-${(_uid += 1)}`).current;
  const triggerId = idProp || `${uid}-trigger`;
  const listId = `${uid}-list`;

  const selected = contacts.find((c) => c.id === value) || null;

  const updateRect = useCallback((): void => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Abre pra cima quando falta espaço embaixo (ex.: seção no rodapé, perto do
    // chat/botão) — evita sobrepor. Senão, abre pra baixo (padrão).
    const openUp = spaceBelow < 300 && spaceAbove > spaceBelow;
    const avail = (openUp ? spaceAbove : spaceBelow) - 16;
    setRect({
      topDown: r.bottom + window.scrollY + 4,
      topUp: r.top + window.scrollY - 4,
      left: r.left + window.scrollX,
      width: r.width,
      openUp,
      maxHeight: Math.max(180, Math.min(320, avail)),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent): void => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node) &&
        listRef.current &&
        !listRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onScroll = (): void => updateRect();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updateRect);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  // Calcula a posição sempre que abre (por clique OU teclado).
  useEffect(() => {
    if (open) updateRect();
  }, [open, updateRect]);

  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const item = listRef.current?.children[focusedIndex] as HTMLElement | null;
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [focusedIndex, open]);

  const handleTriggerClick = (): void => {
    if (disabled) return;
    if (!open) {
      updateRect();
      setFocusedIndex(
        selected ? contacts.findIndex((c) => c.id === value) : -1
      );
    }
    setOpen((prev) => !prev);
  };

  const handleSelect = (contact: Contact): void => {
    onChange?.(contact);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setFocusedIndex(
            selected ? contacts.findIndex((c) => c.id === value) : 0
          );
        } else if (focusedIndex >= 0) {
          handleSelect(contacts[focusedIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setFocusedIndex(0);
          break;
        }
        setFocusedIndex((prev) =>
          prev + 1 < contacts.length ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : prev));
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <SelectWrapper
      ref={wrapperRef}
      $fullWidth={fullWidth}
      className={className}
      style={style}
    >
      <SelectTrigger
        ref={triggerRef}
        type="button"
        id={triggerId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label="Selecionar contato"
        $open={open}
        $hasValue={!!selected}
        $fullWidth={fullWidth}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <TriggerText>{selected ? selected.name : placeholder}</TriggerText>
        <ChevronWrap $open={open}>
          <ChevronIcon />
        </ChevronWrap>
      </SelectTrigger>

      {open &&
        rect &&
        ReactDOM.createPortal(
          <ContactList
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Contatos"
            style={{
              position: 'absolute',
              top: rect.openUp ? rect.topUp : rect.topDown,
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
              transform: rect.openUp ? 'translateY(-100%)' : undefined,
              zIndex: 9999,
            }}
          >
            {contacts.map((c, idx) => (
              <ContactItem
                key={c.id}
                role="option"
                aria-selected={c.id === value}
                $focused={focusedIndex === idx}
                $selected={c.id === value}
                onMouseEnter={() => setFocusedIndex(idx)}
                onClick={() => handleSelect(c)}
              >
                <ContactName>{c.name}</ContactName>
                <ContactMeta>{formatMeta(c)}</ContactMeta>
              </ContactItem>
            ))}
          </ContactList>,
          document.body
        )}
    </SelectWrapper>
  );
};

export default ContactSelect;
